import React, { useState } from 'react'
import { Search, Plus, Users, Eye, Edit, Trash2, MoreHorizontal, Phone, Mail, UserCheck, UserPlus, Key, Smartphone } from 'lucide-react'
import { EmployeeForm } from '../components/employees/EmployeeForm'
import { DeleteEmployeeDialog } from '../components/employees/DeleteEmployeeDialog'
import { CreateInvitationDialog } from '../components/employees/CreateInvitationDialog'
import { ResetPasswordDialog } from '../components/employees/ResetPasswordDialog'
import { ResetDeviceBindingDialog } from '../components/employees/ResetDeviceBindingDialog'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Select,
  SelectOption,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Pagination,
} from '../components/ui'
import { LoadingTable } from '../components/ui/loading-states'
import { ErrorState } from '../components/ui/error-state'
import { useEmployees, type Employee, type EmployeeFilters } from '../hooks/useEmployees'
import { useDepartmentsForSelect } from '../hooks/useDepartments'
import { useOrganizationsForSelect } from '../hooks/organizations/useOrganizations'
import { useLatestSync } from '../hooks/useSyncStatus'
import { APP_TIME_ZONE } from '@/utils/dateFormat'

export function Employees() {
  const [filters, setFilters] = useState<EmployeeFilters>({
    page: 1,
    limit: 20,
    sort: 'last_name:asc',
  })

  const [searchTerm, setSearchTerm] = useState('')
  const [organizationFilter, setOrganizationFilter] = useState<string>('')
  const [departmentFilter, setDepartmentFilter] = useState<string>('')

  // CRUD state
  const [showEmployeeForm, setShowEmployeeForm] = useState(false)
  const [employeeFormMode, setEmployeeFormMode] = useState<'create' | 'edit'>('create')
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [employeeToDelete, setEmployeeToDelete] = useState<Employee | null>(null)
  const [showCreateInvitationDialog, setShowCreateInvitationDialog] = useState(false)
  const [employeeForInvitation, setEmployeeForInvitation] = useState<Employee | null>(null)
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false)
  const [employeeForPasswordReset, setEmployeeForPasswordReset] = useState<Employee | null>(null)
  const [showResetDeviceBindingDialog, setShowResetDeviceBindingDialog] = useState(false)
  const [employeeForDeviceReset, setEmployeeForDeviceReset] = useState<Employee | null>(null)

  // Apply filters directly without debouncing
  React.useEffect(() => {
    setFilters(prev => ({
      ...prev,
      page: 1,
      search: searchTerm || undefined,
      organizationId: organizationFilter || undefined,
      departmentId: departmentFilter || undefined,
    }))
  }, [searchTerm, organizationFilter, departmentFilter])

  const { data, isLoading, error, refetch } = useEmployees(filters)
  const { data: departments } = useDepartmentsForSelect(organizationFilter || null)
  const { data: organizations } = useOrganizationsForSelect()
  const { data: syncData } = useLatestSync()

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }))
  }

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
    setFilters(prev => ({ ...prev, page: 1, search: e.target.value || undefined }))
  }

  const handleOrganizationFilter = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    setOrganizationFilter(value)
    // Сбрасываем подразделение при смене организации
    setDepartmentFilter('')
    setFilters(prev => ({
      ...prev,
      page: 1,
      organizationId: value || undefined,
      departmentId: undefined,
    }))
  }

  const handleDepartmentFilter = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    setDepartmentFilter(value)
    setFilters(prev => ({
      ...prev,
      page: 1,
      departmentId: value || undefined
    }))
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      timeZone: APP_TIME_ZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
  }

  const formatSyncTime = (syncData: any) => {
    if (!syncData?.last_sync_at) {
      return 'Нет данных'
    }

    const syncTime = new Date(syncData.last_sync_at)
    const now = new Date()
    const diffMs = now.getTime() - syncTime.getTime()
    const diffMinutes = Math.floor(diffMs / (1000 * 60))
    const diffHours = Math.floor(diffMinutes / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffMinutes < 1) {
      return 'Только что'
    } else if (diffMinutes < 60) {
      return `${diffMinutes} мин назад`
    } else if (diffHours < 24) {
      return `${diffHours} ч назад`
    } else if (diffDays < 7) {
      return `${diffDays} дн назад`
    } else {
      return formatDate(syncData.last_sync_at)
    }
  }

  const getSourceBadge = (employee: Employee) => {
    if (employee.code_1c) {
      return <Badge variant="secondary">1С</Badge>
    }
    return <Badge variant="outline">TCO</Badge>
  }

  const getStatusBadge = (employee: Employee) => {
    if (employee.is_active) {
      return <Badge variant="default" className="bg-green-100 text-green-800">Активный</Badge>
    }
    return <Badge variant="secondary" className="bg-red-100 text-red-800">Неактивный</Badge>
  }

  const getFullName = (employee: Employee) => {
    const parts = [employee.last_name, employee.first_name, employee.middle_name].filter(Boolean)
    return parts.join(' ')
  }

  // CRUD handlers
  const handleCreateEmployee = () => {
    setSelectedEmployee(null)
    setEmployeeFormMode('create')
    setShowEmployeeForm(true)
  }

  const handleEditEmployee = (employee: Employee) => {
    setSelectedEmployee(employee)
    setEmployeeFormMode('edit')
    setShowEmployeeForm(true)
  }

  const handleDeleteEmployee = (employee: Employee) => {
    setEmployeeToDelete(employee)
    setShowDeleteDialog(true)
  }

  const handleCloseEmployeeForm = () => {
    setShowEmployeeForm(false)
    setSelectedEmployee(null)
  }

  const handleCloseDeleteDialog = () => {
    setShowDeleteDialog(false)
    setEmployeeToDelete(null)
  }

  const handleCreateInvitation = (employee: Employee) => {
    setEmployeeForInvitation(employee)
    setShowCreateInvitationDialog(true)
  }

  const handleCloseCreateInvitationDialog = () => {
    setShowCreateInvitationDialog(false)
    setEmployeeForInvitation(null)
  }

  const handleResetPassword = (employee: Employee) => {
    setEmployeeForPasswordReset(employee)
    setShowResetPasswordDialog(true)
  }

  const handleCloseResetPasswordDialog = () => {
    setShowResetPasswordDialog(false)
    setEmployeeForPasswordReset(null)
  }

  const handleResetDeviceBinding = (employee: Employee) => {
    setEmployeeForDeviceReset(employee)
    setShowResetDeviceBindingDialog(true)
  }

  const handleCloseResetDeviceBindingDialog = () => {
    setShowResetDeviceBindingDialog(false)
    setEmployeeForDeviceReset(null)
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Сотрудники</h1>
          <p className="text-muted-foreground">
            Управление записями сотрудников и информацией
          </p>
        </div>
        <ErrorState
          title="Ошибка загрузки сотрудников"
          onAction={refetch}
        />
      </div>
    )
  }

  return (
    <div className="space-y-8 flex-1 overflow-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-h1 gradient-text-blue">Сотрудники</h1>
          <p className="text-muted-foreground mt-2 text-body-lg">
            Управление записями сотрудников и информацией
          </p>
        </div>
        <Button onClick={handleCreateEmployee} size="lg" variant="gradient">
          <Plus className="mr-2 h-5 w-5" />
          Добавить сотрудника
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Фильтры и поиск</CardTitle>
          <CardDescription>
            Используйте фильтры для поиска нужных сотрудников
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Поиск по имени, ИИН..."
                value={searchTerm}
                onChange={handleSearch}
                className="pl-10"
              />
            </div>

            {/* Organization filter */}
            <Select
              placeholder="Организация"
              value={organizationFilter}
              onChange={handleOrganizationFilter}
            >
              <SelectOption value="">Все организации</SelectOption>
              {organizations?.map((org: { id: string; name: string }) => (
                <SelectOption key={org.id} value={org.id}>
                  {org.name}
                </SelectOption>
              ))}
            </Select>

            {/* Department filter */}
            <Select
              placeholder="Подразделение"
              value={departmentFilter}
              onChange={handleDepartmentFilter}
              disabled={!organizationFilter}
            >
              <SelectOption value="">
                {organizationFilter ? 'Все подразделения' : 'Сначала выберите организацию'}
              </SelectOption>
              {departments?.map((dept) => (
                <SelectOption key={dept.id} value={dept.id}>
                  {dept.name}
                </SelectOption>
              ))}
            </Select>

            {/* Sort */}
            <Select
              value={filters.sort}
              onChange={(e) => setFilters(prev => ({ ...prev, sort: e.target.value }))}
            >
              <SelectOption value="last_name:asc">По фамилии (А-Я)</SelectOption>
              <SelectOption value="last_name:desc">По фамилии (Я-А)</SelectOption>
              <SelectOption value="hire_date:desc">По дате найма (новые)</SelectOption>
              <SelectOption value="hire_date:asc">По дате найма (старые)</SelectOption>
              <SelectOption value="employee_number:asc">По табельному номеру</SelectOption>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Statistics */}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="hover-lift">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-3xl font-bold text-foreground mb-1">{data.total}</p>
                  <p className="text-sm text-muted-foreground">Всего сотрудников</p>
                </div>
                <div className="p-3 bg-tco-blue-100 rounded-xl">
                  <Users className="h-6 w-6 text-tco-blue" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="hover-lift">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-3xl font-bold text-foreground mb-1">
                    {data.activeCount}
                  </p>
                  <p className="text-sm text-muted-foreground">Активные</p>
                </div>
                <div className="p-3 bg-success-light rounded-xl">
                  <UserCheck className="h-6 w-6 text-success" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="hover-lift">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-3xl font-bold text-foreground mb-1">
                    {data.from1cCount}
                  </p>
                  <p className="text-sm text-muted-foreground">Из 1С</p>
                </div>
                <div className="p-3 bg-info-light rounded-xl">
                  <Badge variant="secondary" className="text-xs">1С</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="hover-lift">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Последняя синхронизация</p>
                  <p className="text-lg font-semibold text-foreground">
                    {formatSyncTime(syncData)}
                  </p>
                  {syncData?.last_sync_type && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {syncData.last_sync_type}
                    </p>
                  )}
                </div>
                <div className="p-3 bg-warning-light rounded-xl">
                  <Eye className="h-6 w-6 text-warning" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>Список сотрудников</CardTitle>
          <CardDescription>
            {data && `Показано ${data.data.length} из ${data.total} сотрудников`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingTable rows={filters.limit || 20} />
          ) : data && data.data.length > 0 ? (
            <div className="space-y-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Сотрудник</TableHead>
                    <TableHead>Организация</TableHead>
                    <TableHead>Подразделение и должность</TableHead>
                    <TableHead>Контакты</TableHead>
                    <TableHead>Статус</TableHead>
                    <TableHead>Источник</TableHead>
                    <TableHead>Дата найма</TableHead>
                    <TableHead className="text-right">Действия</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.data.map((employee) => (
                    <TableRow key={employee.id}>
                      <TableCell>
                        <div className="space-y-1">
                          <p className="font-medium">{getFullName(employee)}</p>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <span>№{employee.employee_number}</span>
                            {employee.iin && (
                              <>
                                <span>•</span>
                                <span>ИИН: {employee.iin}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {employee.organization ? (
                          <span className="text-sm">{employee.organization.name}</span>
                        ) : (
                          <span className="text-muted-foreground text-sm">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {employee.department && (
                            <p className="font-medium">{employee.department.name}</p>
                          )}
                          {employee.position && (
                            <p className="text-sm text-muted-foreground">{employee.position.name}</p>
                          )}
                          {!employee.department && !employee.position && (
                            <span className="text-muted-foreground">Не назначено</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {employee.email && (
                            <div className="flex items-center gap-1 text-sm">
                              <Mail className="h-3 w-3" />
                              <span>{employee.email}</span>
                            </div>
                          )}
                          {employee.phone && (
                            <div className="flex items-center gap-1 text-sm">
                              <Phone className="h-3 w-3" />
                              <span>{employee.phone}</span>
                            </div>
                          )}
                          {!employee.email && !employee.phone && (
                            <span className="text-muted-foreground text-sm">Не указаны</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(employee)}
                      </TableCell>
                      <TableCell>
                        {getSourceBadge(employee)}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">
                          {formatDate(employee.hire_date)}
                        </span>
                        {employee.fire_date && (
                          <p className="text-sm text-red-600">
                            Уволен: {formatDate(employee.fire_date)}
                          </p>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center gap-2 justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Просмотр сотрудника"
                            onClick={() => {/* TODO: implement view */}}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {employee.user_id ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              title="Сбросить пароль"
                              onClick={() => handleResetPassword(employee)}
                            >
                              <Key className="h-4 w-4" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              title="Создать код приглашения"
                              onClick={() => handleCreateInvitation(employee)}
                            >
                              <UserPlus className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Редактировать сотрудника"
                            onClick={() => handleEditEmployee(employee)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Удалить сотрудника"
                            onClick={() => handleDeleteEmployee(employee)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                          {employee.user_id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              title="Сбросить привязку устройства"
                              onClick={() => handleResetDeviceBinding(employee)}
                            >
                              <Smartphone className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Дополнительные действия"
                            onClick={() => {/* TODO: implement menu */}}
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {data.totalPages > 1 && (
                <div className="flex justify-center">
                  <Pagination
                    currentPage={data.page}
                    totalPages={data.totalPages}
                    onPageChange={handlePageChange}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <Users className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <h3 className="mt-2 text-sm font-semibold text-muted-foreground">
                Сотрудники не найдены
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Попробуйте изменить фильтры поиска или добавьте нового сотрудника.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Employee Form Dialog */}
      <EmployeeForm
        open={showEmployeeForm}
        onOpenChange={handleCloseEmployeeForm}
        employee={selectedEmployee}
        mode={employeeFormMode}
      />

      {/* Delete Employee Dialog */}
      <DeleteEmployeeDialog
        open={showDeleteDialog}
        onOpenChange={handleCloseDeleteDialog}
        employee={employeeToDelete}
      />

      {/* Create Invitation Dialog */}
      <CreateInvitationDialog
        open={showCreateInvitationDialog}
        onOpenChange={handleCloseCreateInvitationDialog}
        employee={employeeForInvitation}
      />

      {/* Reset Password Dialog */}
      <ResetPasswordDialog
        open={showResetPasswordDialog}
        onOpenChange={handleCloseResetPasswordDialog}
        employee={employeeForPasswordReset}
      />

      {/* Reset Device Binding Dialog */}
      <ResetDeviceBindingDialog
        open={showResetDeviceBindingDialog}
        onOpenChange={handleCloseResetDeviceBindingDialog}
        employee={employeeForDeviceReset}
      />
    </div>
  )
}